import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { Agent } from "src/app/models/agent.model";
import { Control } from "src/app/models/control.model";
import { ControlAgentDto } from "src/app/models/controlAgent.model";
import { Lookup } from "src/app/models/lookup.model";
import { AgentService } from "src/app/services/agent.service";
import { ControlService } from "src/app/services/control.service";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    FormField,
    MultiSelectionFormField,
} from "src/app/shared/form/formField.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { AGENT_OPTION_LABEL_MAP } from "src/app/shared/form/employee-field-helpers";
import { AgentsListComponent } from "src/app/components/agents-list/agents-list.component";

export interface IControlToAgentDialogData extends IFormDialogData {
    id: string;
    name: string;
    code: string;
    controlAgents?: string[] | ControlAgentDto[];
}

type AgentIdInput = string | ControlAgentDto;

@Component({
  standalone: false,
    selector: "app-control-to-agent-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class ControlToAgentDialogComponent
    extends FormComponent<Control>
    implements OnInit
{
    agentsList: Lookup[] = [];
    filteredAgents: Lookup[] = [];

    constructor(
        dialogRef: MatDialogRef<ControlToAgentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IControlToAgentDialogData,
        protected agentService: AgentService,
        protected controlService: ControlService,
        protected injector: Injector,
    ) {
        super(dialogRef, controlService, data, "Agent", injector);
        this.title = this.translateService.getDynamicTranslation(
            "Manage Agents for Control",
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({ agents: this.agentService.getAll() }).subscribe({
            next: (result) => {
                this.entity = this.data as any;
                this.agentsList = result.agents;
                this.filteredAgents = [...result.agents];
                this.normalizeAgentIds();
                this.initFields();
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    getNewObject(): Control {
        return new Control();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        const agentSearchFields = [
            nameOf<Agent>((x) => x.name),
            nameOf<Agent>((x) => x.casId),
        ];

        this.fields = [
            new MultiSelectionFormField({
                property: nameOf((x: Control) => x.controlAgents),
                name: "Select Agents",
                options: this.filteredAgents,
                cssClass: "vertical-checkbox-options",
                searchKeys: agentSearchFields,
                searchComponent: AgentsListComponent,
                selectionTemplate: (opt: FormField) =>
                    this.getSelectionTemplate(opt),
                optionTemplateFields: agentSearchFields,
                optionTemplateLabelMap: { ...AGENT_OPTION_LABEL_MAP },
            }),
        ];
    }

    override onBeforeSave(model: Control): void {
        model.controlAgents = this.convertToAgentDtos(model.controlAgents);
    }

    override saveAction(model: Control) {
        return this.service.patch(this.entity.id, model);
    }

    override afterSaveAction(response: any): void {
        this.isLoading = false;
        this.toastService.showSuccess(
            "Control agents updated successfully",
            true,
        );
        this.closeDialog(response);
    }

    private getSelectionTemplate(opt: FormField): string {
        const selectedAgents = this.entity[opt.property];
        if (!selectedAgents?.length) return "No agents selected";

        return opt.options
            .filter((res) => selectedAgents.includes(res.id))
            .map((res) => res.name)
            .join(", ");
    }

    private normalizeAgentIds(): void {
        if (!this.entity.controlAgents?.length) return;

        const firstItem = this.entity.controlAgents[0] as AgentIdInput;
        if (typeof firstItem === "object" && "agentId" in firstItem) {
            const numberIds = (
                this.entity.controlAgents as ControlAgentDto[]
            ).map((dto) => dto.agentId);
            this.entity.controlAgents = numberIds as any;
        }
    }

    private convertToAgentDtos(agentIds: ControlAgentDto[]): ControlAgentDto[] {
        return (agentIds as AgentIdInput[]).map((item) => {
            const agentId = typeof item === "string" ? item : item.agentId;
            const agent = this.agentsList.find((a) => a.id === agentId);
            return {
                agentId,
                controlId: this.entity.id,
                agentName: agent?.name || "",
            };
        });
    }
}
